import { Alert, AlertDescription, AlertTitle, Button, Skeleton } from '@folio/components';
import { cn } from '@folio/lib';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { EmailAddressDto, GetEmailOutput } from 'korint-sdk';
import {
  emailControllerGetAttachmentDownloadUrl,
  emailControllerGetEmailQueryOptions,
} from 'korint-sdk';
import { Download, Loader2, Paperclip } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { EmailHtmlFrame } from './EmailHtmlFrame';
import { renderEmailHtml } from './render-email-html';
import { Text } from '@folio/components/ui/text';
import { Stack } from '@folio/components/ui/flex';

type Attachment = GetEmailOutput['attachments'][number];

const POLL_INTERVAL_MS = 3000;

const ATTACHMENT_FAILURE_REASONS = [
  'not_found',
  'size_limit_exceeded',
  'account_size_limit_exceeded',
] as const;

const hasWorkInFlight = (email: GetEmailOutput) =>
  email.bodyStatus === 'pending' || email.attachments.some(({ status }) => status === 'pending');

const formatBytes = (bytes: number, locale: string) => {
  const units = ['byte', 'kilobyte', 'megabyte'] as const;
  const exponent = Math.min(units.length - 1, Math.floor(Math.log10(Math.max(bytes, 1)) / 3));
  return new Intl.NumberFormat(locale, {
    style: 'unit',
    unit: units[exponent],
    maximumFractionDigits: exponent === 0 ? 0 : 1,
  }).format(bytes / 1000 ** exponent);
};

const addressLabel = ({ name, address }: EmailAddressDto) =>
  name && address ? `${name} <${address}>` : name || address || '—';

function AddressLine({ label, addresses }: { label: string; addresses: EmailAddressDto[] }) {
  if (addresses.length === 0) return null;
  return (
    <Text size="xs" tone="muted">
      {label} : <span className="break-words">{addresses.map(addressLabel).join(', ')}</span>
    </Text>
  );
}

function useAttachmentFailureLabel() {
  const { t } = useTranslation();
  return (reason: string | null | undefined) =>
    reason && (ATTACHMENT_FAILURE_REASONS as readonly string[]).includes(reason)
      ? t(`emailDetail.attachments.reason.${reason}`)
      : reason || t('emailDetail.attachments.reason.unknown');
}

function AttachmentChip({ emailId, attachment }: { emailId: string; attachment: Attachment }) {
  const { t, i18n } = useTranslation();
  const failureLabel = useAttachmentFailureLabel();
  const { mutate: download, isPending: isDownloading } = useMutation({
    mutationFn: () => emailControllerGetAttachmentDownloadUrl(emailId, attachment.id),
    onSuccess: ({ url }) => {
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.rel = 'noopener';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    },
  });
  const failed = attachment.status === 'failed' || attachment.status === 'skipped';

  return (
    <li
      className={cn(
        'inline-flex items-center gap-2 rounded-md border bg-background px-3 py-1.5 text-sm',
        failed && 'border-destructive/40 text-destructive',
      )}
      data-testid="email-attachment"
      data-status={attachment.status}
    >
      <Paperclip className="size-3.5 shrink-0" />
      <Text inline truncate weight="medium" className="max-w-64">
        {attachment.filename ?? t('emailDetail.attachments.unnamed')}
      </Text>
      <Text inline size="xs" tone={failed ? undefined : 'muted'}>
        · {formatBytes(attachment.encodedSize, i18n.language)}
        {attachment.isInline && !failed && ` · ${t('emailDetail.attachments.inline')}`}
        {attachment.status === 'failed' &&
          ` · ${t('emailDetail.attachments.failed', { reason: failureLabel(attachment.failureReason) })}`}
        {attachment.status === 'skipped' &&
          ` · ${t('emailDetail.attachments.skipped', { reason: failureLabel(attachment.failureReason) })}`}
      </Text>
      {attachment.status === 'pending' && (
        <Loader2
          className="size-3.5 animate-spin text-muted-foreground"
          aria-label={t('emailDetail.attachments.pending')}
        />
      )}
      {attachment.status === 'stored' && (
        <Button
          variant="ghost"
          size="icon"
          className="size-6"
          disabled={isDownloading}
          onClick={() => download()}
          aria-label={t('emailDetail.attachments.download')}
        >
          <Download className="size-3.5" />
        </Button>
      )}
    </li>
  );
}

function EmailBody({ email }: { email: GetEmailOutput }) {
  const { t } = useTranslation();
  const html = useMemo(() => {
    if (email.bodyStatus !== 'stored' || email.bodyHtml == null) return null;
    const inlineSources = Object.fromEntries(
      email.attachments.flatMap(({ contentId, inlineUrl }) =>
        contentId && inlineUrl ? [[contentId, inlineUrl]] : [],
      ),
    );
    return renderEmailHtml(email.bodyHtml, inlineSources, t('emailDetail.body.inlineUnavailable'));
  }, [email, t]);

  switch (email.bodyStatus) {
    case 'pending':
      return (
        <Stack gap={2} data-testid="email-body" data-status="pending">
          {email.preview && (
            <Text size="sm" tone="muted">
              {email.preview}
            </Text>
          )}
          <Text size="sm" tone="muted" className="inline-flex items-center gap-2">
            <Loader2 className="size-4 animate-spin" />
            {t('emailDetail.body.pending')}
          </Text>
        </Stack>
      );
    case 'failed':
      return (
        <Alert variant="destructive" data-testid="email-body" data-status="failed">
          <AlertTitle>{t('emailDetail.body.failed.title')}</AlertTitle>
          {email.bodyError && <AlertDescription>{email.bodyError}</AlertDescription>}
        </Alert>
      );
    case 'unavailable':
      return (
        <Alert data-testid="email-body" data-status="unavailable">
          <AlertTitle>{t('emailDetail.body.unavailable.title')}</AlertTitle>
          <AlertDescription>{t('emailDetail.body.unavailable.description')}</AlertDescription>
        </Alert>
      );
    case 'stored':
      return html ? (
        <EmailHtmlFrame html={html} title={email.subject || t('generalInbox.noSubject')} />
      ) : (
        <Text
          size="sm"
          tone="muted"
          className="italic"
          data-testid="email-body"
          data-status="stored"
        >
          {t('emailDetail.body.empty')}
        </Text>
      );
  }
}

// Expanded content of an email row: the row itself already shows sender, subject and date
export function EmailDetail({
  emailId,
  onRead,
}: {
  emailId: string;
  // Fires once the read is acknowledged, so a list can repaint the row without a refetch
  onRead?: (emailId: string) => void;
}) {
  const { t } = useTranslation();
  const {
    data: email,
    isPending,
    isError,
  } = useQuery({
    ...emailControllerGetEmailQueryOptions(emailId),
    refetchInterval: query =>
      query.state.data && hasWorkInFlight(query.state.data) ? POLL_INTERVAL_MS : false,
  });

  const isRead = email?.isRead ?? false;
  useEffect(() => {
    if (isRead) onRead?.(emailId);
  }, [isRead, emailId, onRead]);

  if (isError) {
    return (
      <Text size="sm" tone="destructive">
        {t('emailDetail.error')}
      </Text>
    );
  }
  if (isPending) {
    return (
      <Stack gap={2} data-testid="email-detail-loading">
        <Skeleton className="h-3 w-1/3" />
        <Skeleton className="h-20 w-full" />
      </Stack>
    );
  }

  return (
    <Stack asChild gap={3}>
      <article data-testid="email-detail">
        <Stack className="gap-0.5">
          <AddressLine label={t('emailDetail.from')} addresses={email.fromAddresses} />
          <AddressLine label={t('emailDetail.to')} addresses={email.toAddresses} />
          <AddressLine label={t('emailDetail.cc')} addresses={email.ccAddresses} />
          <AddressLine label={t('emailDetail.bcc')} addresses={email.bccAddresses} />
        </Stack>

        <EmailBody email={email} />

        {email.attachments.length > 0 && (
          <ul className="flex flex-wrap gap-2">
            {email.attachments.map(attachment => (
              <AttachmentChip key={attachment.id} emailId={email.id} attachment={attachment} />
            ))}
          </ul>
        )}
      </article>
    </Stack>
  );
}
